/**
 * useSessionUnpaidPostpay — 세션 미결제 후불 조회 공유 훅 (#603 [2][3] append 정합 → #654 N건 정합).
 *
 * 테이블 후불 세션의 미결제 주문서를 R5 `unpaid-postpay` 로 조회해
 * 진행바([2])와 세션 시트([3]), 주문내역 세션 섹션([3]) 이 **단일 출처**로 공유한다.
 * 중복 fetch 로직(기존 OrderHistoryPage 인라인 effect)을 회피하기 위한 추출.
 *
 * ## #654: append 전제(Open 주문서 1건) 폐기 — orders N건 정합
 *   기존 §0 은 "미결제 = Open 주문서 1건"을 전제로 `orders[0]` 만 추출했으나, 실제 세션에
 *   미결제 Order N건이 존재하는 경로가 있다(dev 실측 2건 — 합계는 N건 합산이라 정확,
 *   아이템은 [0]만 표시되는 불일치). admin R5 응답은 처음부터 orders 배열(N건)이 계약.
 *   - `orders`: 주문별 구조 보존 (시트의 주문별 섹션 표시)
 *   - `items`: **전 주문 라인 flatten** (진행바 건수·시트 합계 라인이 N건 합산과 정합)
 *   - `adjustmentOrderId`: 보정치(팁/포인트) 부여 대상 — **정확히 1건일 때만** orders[0].id,
 *     N건이면 null (fail-safe — 임의로 첫 주문에 부여하는 silent 동작 금지)
 *   - `storeId`: orders[0].store_id — 테이블 세션은 단일 매장 스코프(테이블 = 매장 소속)라
 *     N건이어도 모든 주문이 같은 매장. 포인트 조회 용도로 안전.
 *   - `totalAmount`: 서버 권위 합계 (FE 재합산 금지)
 *
 * ## fail-closed
 *   토큰 부재 → null + `tokenMissing=true` (조회 미호출, 결제 도달성 회복 안내 트리거).
 *   0건 / orders 비었음(계약 위반) / 조회 실패 → null (배너·진행바 미표시).
 *
 * ## 인증 (R5)
 *   회원은 apiClient Authorization(자동), 비회원은 `guest_password` 를 X-Guest-Password 헤더로.
 *   회원이면 guestPassword 를 미전달한다(GET/모달/POST 정합 통일 — 회원 모드 일관).
 *
 * @see src/lib/api/payments.ts tableSessionApi.getSessionUnpaidPostpay
 * @see src/components/OrderHistoryPage.tsx (세션 섹션 — 본 훅 소비)
 * @see src/components/session/SessionProgressBar.tsx ([2] 진행바 — 본 훅 소비)
 * @see docs/plans/2026-06-04-postpay-table-order-ux-redesign-design.md §0/§3 [2][3]
 */
import { useCallback, useEffect, useState } from 'react';
import { tableSessionApi } from '@/lib/api/payments';
import { getTableSessionFromStorage } from '@/lib/tableContext';
import type { OrderItemDetail } from '@/lib/api/types';
import { logger } from '@/utils/logger';
import { setSplitPaymentAvailable as persistSplitPaymentAvailable } from '@/lib/comensalAccess';

/**
 * 세션 미결제 주문 1건의 표시용 요약 (#654 — 시트의 주문별 섹션 단위).
 */
export interface SessionUnpaidOrder {
  /** 주문 ID */
  id: number;
  /** 주문 번호 (예: ORD-IDI-20260610-0016) — 시트 섹션 라벨. null 이면 #id 폴백 */
  orderNumber: string | null;
  /** 해당 주문의 라인 전체 (주문별 구조 보존) */
  items: OrderItemDetail[];
  /**
   * 주문 합계 (서버 권위 `order.total` — #661 전용 페이지의 주문별 소계 표시).
   *
   * R5 응답이 OrderResource 전체 계약이라 항상 제공되나, 방어적으로 숫자가 아니면
   * null (표시 생략 — FE 재합산으로 대체하지 않는다).
   */
  total: number | null;
  /**
   * 미결제 주문의 실제 결제 조합 코드.
   *
   * 세션 정산 UI 는 이 값으로 카드 게이트웨이 정산 가능 여부를 fail-closed 판정한다.
   */
  latestPaymentCombinationCode?: string | null;
}

/**
 * 세션 미결제 후불 요약 (#654 — orders N건 정합).
 */
export interface SessionUnpaidPostpay {
  /** 테이블 세션 토큰 (sess_...) */
  token: string;
  /**
   * 보정치(팁/포인트) 부여 대상 단일 주문 ID (§0(D)).
   *
   * #654: 미결제가 **정확히 1건**(count===1 && orders.length===1)일 때만 orders[0].id.
   * N건이면 null — ChargeSessionModal 이 보정치 입력을 숨긴다(fail-safe).
   * 보정치 귀속 주문이 결정 불가인데 임의로 첫 주문에 부여하는 silent 동작은 금지.
   */
  adjustmentOrderId: number | null;
  /** 매장 ID (orders[0].store_id — 세션은 단일 매장 스코프, 사용 가능 포인트 조회) */
  storeId: number;
  /** 미결제 후불 건수 (서버 권위) */
  count: number;
  /** 미결제 후불 합계 (서버 권위 문자열 — FE 재합산 금지) */
  totalAmount: string;
  /** 팁 비율 계산의 서버 권위 소계. legacy 응답은 total_amount로 정규화한다. */
  subtotalAmount: string;
  /** quote에 이미 반영된 팁. Bill 재시도 폼의 초기값으로 사용한다. */
  tipAmount: string;
  expectedBillSubtotalMinor?: number;
  expectedTotalMinor?: number;
  /** 주문별 구조 (#654 — 시트의 주문별 섹션 표시) */
  orders: SessionUnpaidOrder[];
  /** 전 주문 라인 flatten ([2] 진행 건수 = items.length — N건 합산 합계와 정합) */
  items: OrderItemDetail[];
  /**
   * 분할결제를 실제로 쓸 수 있는가 (admin #2326).
   *
   * 서버가 매장 정책 AND 개발 게이트를 합쳐서 내린다. 필드가 없는 구버전 서버에서는
   * **꺼진 것으로 본다** — 모를 때 켜면 쓰지도 않는 매장에 좌석 참여자 행이 생긴다.
   */
  splitPaymentAvailable: boolean;
  /** Bill 범위 quote면 현재 결제 대상. */
  billId?: number | null;
}

/**
 * #654 codex I3: 결제 진입 가능 라인 존재 판정 — 주문별 구조 기준 (시트와 공유하는 SSOT).
 *
 * count>0 인데 라인이 전혀 없으면(계약 위반) 결제 대상 내용을 보여줄 수 없으므로
 * 결제 진입을 차단한다(fail-closed). SessionOrdersSheet 의 CTA 숨김과 동일 판정.
 */
export function ordersHavePayableLines(orders: SessionUnpaidOrder[]): boolean {
  return orders.some((order) => order.items.length > 0);
}

/**
 * #654 codex I3: 훅 data 기준 결제 진입 가능 판정 — 배너 CTA / 헤더 PAY 게이트 공유.
 *
 * 시트(빈 라인 → CTA 숨김)와 직접 결제 진입(배너 "전체 결제" / openCharge)이
 * 서로 다른 조건을 보면 빈 items 계약 위반 시 배너로만 결제가 열리는 불일치가 생긴다.
 */
export function hasPayableSessionLines(data: SessionUnpaidPostpay | null): boolean {
  return data !== null && ordersHavePayableLines(data.orders);
}

export interface UseSessionUnpaidPostpayOptions {
  /**
   * 조회 활성 게이트 — false 면 R5 호출을 생략한다(불필요 API 회피).
   *
   * 호출자가 "미결제 후불 주문 존재" 같은 선행 조건으로 게이팅한다
   * (OrderHistoryPage 는 로컬 후불 미결제 존재, 메뉴 화면은 테이블 모드).
   */
  enabled: boolean;
  /** 회원 여부 — true 면 guestPassword 를 전송하지 않는다(회원 모드 일관). */
  isLoggedIn: boolean;
  /** 비회원 주문 비밀번호 (비회원일 때만 X-Guest-Password 헤더로 전송). */
  guestPassword?: string;
  /** 자기 Bill quote. 생략은 기존 세션 전량 조회다. */
  billId?: number;
  /** Bill 소유자 인증. Bill 조회에서는 guestPassword를 사용하지 않는다. */
  comensalDeviceToken?: string;
}

export interface UseSessionUnpaidPostpayReturn {
  /** 미결제 후불 요약 — 미조회/실패/0건/토큰부재 시 null (fail-closed). */
  data: SessionUnpaidPostpay | null;
  /** 조회 진행 중 여부. */
  isLoading: boolean;
  /**
   * 미결제 후불은 있으나 세션 토큰(QR)이 유실되어 조회 불가일 때 true.
   * 호출자가 결제 도달성 회복(QR 재스캔) 안내에 사용한다(#603 [4] Codex Critical).
   */
  tokenMissing: boolean;
  /** 주문이 0건이어도 보존하는 현재 세션 토큰. */
  sessionToken?: string | null;
  /** 주문 data와 독립적인 서버 가용성. null은 아직 모름/조회 실패. */
  splitPaymentAvailable?: boolean | null;
  /** 강제 재조회 (결제 성공 후 등). */
  refetch: () => void;
}

export function useSessionUnpaidPostpay({
  enabled,
  isLoggedIn,
  guestPassword,
  billId,
  comensalDeviceToken,
}: UseSessionUnpaidPostpayOptions): UseSessionUnpaidPostpayReturn {
  const [data, setData] = useState<SessionUnpaidPostpay | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [tokenMissing, setTokenMissing] = useState(false);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [splitPaymentAvailable, setSplitPaymentAvailable] = useState<boolean | null>(null);
  // 강제 재조회 트리거 (refetch 시 증가).
  const [reloadKey, setReloadKey] = useState(0);

  // 회원이면 guest_password 미전송 (GET/모달/POST 정합 — 회원 모드 일관성 보장).
  const effectiveGuestPassword = isLoggedIn ? undefined : guestPassword;

  useEffect(() => {
    // 비활성 게이트 → 조회 생략 + 초기화(fail-closed).
    if (!enabled) {
      setData(null);
      setTokenMissing(false);
      setIsLoading(false);
      setSessionToken(null);
      setSplitPaymentAvailable(null);
      return;
    }

    const tableSession = getTableSessionFromStorage();

    // 토큰 부재 → 조회 불가. tokenMissing=true 로 결제 도달성 회복 안내를 트리거한다.
    if (!tableSession) {
      setData(null);
      setTokenMissing(true);
      setIsLoading(false);
      setSessionToken(null);
      setSplitPaymentAvailable(null);
      return;
    }

    setTokenMissing(false);
    setSessionToken(tableSession.token);
    setIsLoading(true);

    let cancelled = false;

    tableSessionApi
      .getSessionUnpaidPostpay(
        tableSession.token,
        billId === undefined ? effectiveGuestPassword : undefined,
        { billId, comensalDeviceToken },
      )
      .then((res) => {
        if (cancelled) return;
        const available = res.split_payment_available === true;
        setSplitPaymentAvailable(available);
        persistSplitPaymentAvailable(available);
        // #654: orders 전체(N건)를 주문별 구조로 보존 + 라인 flatten(진행바·시트 합계 정합).
        // count>0 이라도 orders 가 비었으면(계약 위반) fail-closed — 표시 데이터 구성 불가.
        const firstOrder = res.orders[0];
        const billScopeMatches = billId === undefined || res.bill?.id === billId;
        const quotePreconditionPresent = comensalDeviceToken === undefined
          || (billId === undefined
            ? Number.isSafeInteger(res.expected_total_minor)
            : Number.isSafeInteger(res.expected_bill_subtotal_minor));
        if (res.count > 0 && firstOrder && billScopeMatches && quotePreconditionPresent) {
          // P2(codex): 테이블 세션 = 단일 매장 전제 방어 — store_id 혼재(계약 위반)면
          // 로그만 남기고 표시는 진행한다(storeId 는 포인트 조회 용도라 차단까지는 불요).
          const distinctStoreIds = new Set(res.orders.map((order) => order.store_id));
          if (distinctStoreIds.size > 1) {
            logger.warn(
              '[useSessionUnpaidPostpay] 세션 미결제 주문 store_id 혼재 — 단일 매장 전제 위반',
              [...distinctStoreIds],
            );
          }
          const orders: SessionUnpaidOrder[] = res.orders.map((order) => ({
            id: order.id,
            orderNumber: order.order_number ?? null,
            items: order.items ?? [],
            // #661: 주문별 소계 = 서버 권위 order.total (FE 재합산 금지). 비정상이면 null(표시 생략).
            total: typeof order.total === 'number' && Number.isFinite(order.total) ? order.total : null,
            latestPaymentCombinationCode: order.latest_payment?.combination_code ?? null,
          }));
          setData({
            token: tableSession.token,
            // 보정치 대상은 단일 주문일 때만 결정 가능 — N건이면 null (fail-safe, 타입 주석 참조).
            adjustmentOrderId:
              res.count === 1 && res.orders.length === 1 ? firstOrder.id : null,
            storeId: firstOrder.store_id,
            count: res.count,
            totalAmount: res.total_amount,
            subtotalAmount: res.subtotal_amount ?? res.total_amount,
            tipAmount: res.tip_amount ?? '0.00',
            expectedBillSubtotalMinor: res.expected_bill_subtotal_minor,
            expectedTotalMinor: res.expected_total_minor,
            orders,
            items: orders.flatMap((order) => order.items),
            splitPaymentAvailable: res.split_payment_available === true,
            billId: res.bill?.id ?? null,
          });
        } else {
          setData(null);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // fail-closed: 조회 실패 시 미표시 (진행바/배너 모두 숨김).
        logger.warn(
          '[useSessionUnpaidPostpay] 세션 미결제 후불 조회 실패 — 진행바/배너 미표시',
          err instanceof Error ? new Error(err.message) : undefined,
        );
        setData(null);
        setSplitPaymentAvailable(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // Codex Important(회귀 차단): isLoggedIn 을 deps 에 포함한다. 비회원(게스트비번 없음)→회원
    // 전환 시 effectiveGuestPassword 는 undefined→undefined 로 불변이라 그 의존성만으로는
    // 재조회되지 않는다(이전 null 상태 유지 → 배너/진행바 계속 미노출). 로그인 상태 전환 시
    // 재조회를 보장해 기존 OrderHistoryPage 인라인 fetch 동작을 보존한다.
  }, [
    enabled,
    isLoggedIn,
    effectiveGuestPassword,
    billId,
    comensalDeviceToken,
    reloadKey,
  ]);

  const refetch = useCallback(() => {
    setReloadKey((k) => k + 1);
  }, []);

  return {
    data,
    isLoading,
    tokenMissing,
    sessionToken,
    splitPaymentAvailable,
    refetch,
  };
}
